import { colorStopToString, fixTransparentColorStops } from '../color-utils.js';

/**
 * Render a repeating radial gradient
 * @param {CanvasRenderingContext2D} context - Canvas context
 * @param {Object} gradientDef - Gradient definition from parser
 * @param {Object} bounds - Bounding box { x, y, w, h }
 * @returns {CanvasGradient} Canvas gradient object
 */
export function renderRepeatingRadialGradient(context, gradientDef, { x, y, w, h }) {
  // Parse radial gradient shape and size from orientation
  const orientation = gradientDef.orientation
    ? gradientDef.orientation[0]
    : { type: 'shape', value: 'ellipse' };

  const isCircle = orientation?.type === 'shape' && orientation.value === 'circle';

  // Calculate gradient parameters based on shape
  const centerX = x + w / 2;
  const centerY = y + h / 2;
  const maxRadius = isCircle ? Math.min(w, h) / 2 : Math.max(w, h) / 2;

  // Create radial gradient
  const gradient = context.createRadialGradient(centerX, centerY, 0, centerX, centerY, maxRadius);

  // Clone colorStops to avoid mutating the original
  const colorStops = gradientDef.colorStops.map((stop) => ({ ...stop }));

  // Fix transparent color stops to match CSS premultiplied alpha behavior
  fixTransparentColorStops(colorStops);

  // Calculate pattern length from color stops
  let patternLength = 0;
  colorStops.forEach((stop) => {
    if (stop.length) {
      if (stop.length.type === 'px') {
        patternLength = Math.max(patternLength, parseFloat(stop.length.value));
      } else if (stop.length.type === '%') {
        patternLength = Math.max(patternLength, (parseFloat(stop.length.value) / 100) * maxRadius);
      }
    }
  });

  // If no explicit pattern length, use evenly distributed stops
  if (patternLength === 0) {
    patternLength = maxRadius;
  }

  // Calculate number of repetitions
  const repetitions = Math.ceil(maxRadius / patternLength);

  // Add repeated color stops
  for (let i = 0; i < repetitions; i++) {
    colorStops.forEach((stop, index) => {
      let position = i / repetitions;

      if (stop.length) {
        if (stop.length.type === 'px') {
          position += parseFloat(stop.length.value) / maxRadius / repetitions;
        } else {
          position += parseFloat(stop.length.value) / 100 / repetitions;
        }
      } else {
        position += index / (colorStops.length - 1) / repetitions;
      }

      const colorValue = colorStopToString(stop);

      if (colorValue) {
        gradient.addColorStop(Math.min(position, 1), colorValue);
      }
    });
  }

  return gradient;
}
